// src/public/PublicBookingPage.js
// Customer-facing online booking page — no auth required
// Subscriber branding only — no JobFlow NOW branding anywhere

import React, { useState, useEffect } from 'react';
import { getApiBase } from '../apiBase';

const API_BASE = getApiBase();

const getIsMobile = () => typeof window !== 'undefined' && window.innerWidth < 768;

export default function PublicBookingPage() {
  const [isMobile, setIsMobile] = useState(getIsMobile());
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [business, setBusiness] = useState({});
  const [branding, setBranding] = useState({});
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [hoursSettings, setHoursSettings] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    appointment_type_id: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    service_address: '',
    city: '',
    state: '',
    zip: '',
    notes: '',
    requested_date: '',
    requested_time: ''
  });

  // Extract slug from URL path /book/:slug
  const slug = window.location.pathname.split('/book/')[1]?.split('/')[0] || '';

  useEffect(() => {
    const onResize = () => setIsMobile(getIsMobile());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!slug) { setNotFound(true); setLoading(false); return; }
    loadBookingPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadBookingPage = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/public/book/${slug}`);
      if (!res.ok) { setNotFound(true); return; }
      const data = await res.json();
      setBusiness(data.business || {});
      setBranding(data.branding || {});
      setAppointmentTypes(data.appointmentTypes || []);
      setHoursSettings(data.hours || null);
    } catch (e) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const formatPhone = (value) => {
    const cleaned = String(value || '').replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    setError('');
    if (!form.appointment_type_id) return setError('Please select a service.');
    if (!form.customer_name.trim()) return setError('Please enter your name.');
    if (!form.customer_phone.trim()) return setError('Please enter your phone number.');
    if (!form.customer_email.trim()) return setError('Please enter your email address.');
    if (!form.requested_date) return setError('Please select a date.');
    if (isDateDisabled(form.requested_date)) return setError('We are closed on that day. Please select another date.');
    if (!form.requested_time) return setError('Please select a time.');
    if (blockedSlots.has(form.requested_time)) return setError('That time is unavailable. Please select another time.');

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/public/book/${slug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError(data.error || 'Booking failed. Please try again.');
      }
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const primaryColor = branding.primary_color || '#0a1128';
  const accentColor = branding.secondary_color || '#39FF14';

  const styles = {
    page: {
      minHeight: '100vh',
      backgroundColor: '#f0f4f8',
      fontFamily: "'Segoe UI', sans-serif",
      padding: isMobile ? '0' : '40px 20px'
    },
    card: {
      backgroundColor: '#fff',
      maxWidth: '680px',
      margin: '0 auto',
      borderRadius: isMobile ? '0' : '12px',
      overflow: 'hidden',
      boxShadow: isMobile ? 'none' : '0 4px 24px rgba(0,0,0,0.12)'
    },
    header: {
      backgroundColor: primaryColor,
      padding: isMobile ? '24px 20px' : '32px 40px',
      textAlign: 'center'
    },
    logo: {
      maxWidth: '180px',
      maxHeight: '100px',
      objectFit: 'contain',
      marginBottom: '12px'
    },
    businessName: {
      color: '#fff',
      fontSize: isMobile ? '22px' : '28px',
      fontWeight: 900,
      margin: '0 0 6px 0',
      textTransform: 'uppercase',
      letterSpacing: '1px'
    },
    tagline: {
      color: accentColor,
      fontSize: '13px',
      fontWeight: 700,
      letterSpacing: '1px',
      textTransform: 'uppercase'
    },
    body: {
      padding: isMobile ? '20px' : '40px'
    },
    sectionTitle: {
      fontWeight: 900,
      fontSize: '13px',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      color: '#666',
      marginBottom: '12px',
      marginTop: '24px'
    },
    label: {
      display: 'block',
      fontWeight: 700,
      fontSize: '13px',
      textTransform: 'uppercase',
      color: '#333',
      marginBottom: '6px',
      letterSpacing: '0.5px'
    },
    input: {
      width: '100%',
      padding: '12px 14px',
      border: '2px solid #ddd',
      borderRadius: '8px',
      fontSize: '15px',
      boxSizing: 'border-box',
      outline: 'none',
      marginBottom: '14px',
      fontFamily: 'inherit'
    },
    textarea: {
      width: '100%',
      padding: '12px 14px',
      border: '2px solid #ddd',
      borderRadius: '8px',
      fontSize: '15px',
      boxSizing: 'border-box',
      outline: 'none',
      marginBottom: '14px',
      fontFamily: 'inherit',
      resize: 'vertical',
      minHeight: '100px'
    },
    row2: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
      gap: '12px'
    },
    row3: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
      gap: '12px'
    },
    serviceBtn: {
      width: '100%',
      padding: '14px',
      border: '3px solid #ddd',
      borderRadius: '8px',
      backgroundColor: '#fff',
      cursor: 'pointer',
      fontWeight: 700,
      fontSize: '14px',
      textAlign: 'left',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      transition: 'all 0.15s ease'
    },
    colorDot: {
      width: '14px',
      height: '14px',
      borderRadius: '50%',
      flexShrink: 0
    },
    submitBtn: {
      width: '100%',
      padding: '16px',
      backgroundColor: primaryColor,
      color: accentColor,
      border: `3px solid ${accentColor}`,
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      marginTop: '8px'
    },
    error: {
      backgroundColor: '#fee2e2',
      border: '2px solid #ef4444',
      borderRadius: '8px',
      padding: '12px 14px',
      color: '#991b1b',
      fontSize: '14px',
      fontWeight: 700,
      marginBottom: '16px'
    },
    success: {
      textAlign: 'center',
      padding: isMobile ? '40px 20px' : '60px 40px'
    },
    successIcon: {
      fontSize: '64px',
      marginBottom: '16px'
    },
    successTitle: {
      fontSize: '24px',
      fontWeight: 900,
      color: '#166534',
      marginBottom: '12px'
    },
    successText: {
      fontSize: '15px',
      color: '#555',
      lineHeight: '1.6'
    },
    divider: {
      border: 'none',
      borderTop: '2px solid #f0f0f0',
      margin: '24px 0'
    },
    contactInfo: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '12px',
      justifyContent: 'center',
      marginTop: '16px'
    },
    contactItem: {
      fontSize: '13px',
      color: '#888',
      fontWeight: 600
    }
  };

  // ── Build time slots from hours settings ──────────────────────────────────
  const DAY_NAMES = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];

  const isDateDisabled = (dateStr) => {
    if (!hoursSettings || !dateStr) return false;
    const d = new Date(dateStr + 'T12:00:00');
    const dayName = DAY_NAMES[d.getDay()];
    const dayVal = String(hoursSettings[dayName] || '').trim();
    return !dayVal || dayVal.toLowerCase() === 'closed';
  };

  // Parse "08:00" or "8:00 AM" into total minutes
  const parseToMinutes = (timeStr) => {
    if (!timeStr) return null;
    const s = String(timeStr).toLowerCase().trim();
    const match24 = s.match(/^(\d{1,2}):(\d{2})$/);
    if (match24) return parseInt(match24[1], 10) * 60 + parseInt(match24[2], 10);
    const match12 = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/);
    if (match12) {
      let h = parseInt(match12[1], 10);
      const m = parseInt(match12[2] || '0', 10);
      if (match12[3] === 'pm' && h !== 12) h += 12;
      if (match12[3] === 'am' && h === 12) h = 0;
      return h * 60 + m;
    }
    return null;
  };

  // Get open/close minutes from hoursSettings for a given day name
  const getOpenCloseForDay = (dayName) => {
    if (!hoursSettings || !dayName) return null;
    const dayVal = String(hoursSettings[dayName] || '').trim();
    if (!dayVal || dayVal.toLowerCase() === 'closed') return null;
    const parts = dayVal.split('-').map(s => s.trim());
    if (parts.length < 2) return null;
    const openMin = parseToMinutes(parts[0]);
    const closeMin = parseToMinutes(parts[1]);
    if (openMin === null || closeMin === null) return null;
    return { openMin, closeMin };
  };

  const buildTimeSlots = () => {
    const allSlots = [
      '7:00 AM','7:30 AM','8:00 AM','8:30 AM','9:00 AM','9:30 AM',
      '10:00 AM','10:30 AM','11:00 AM','11:30 AM','12:00 PM','12:30 PM',
      '1:00 PM','1:30 PM','2:00 PM','2:30 PM','3:00 PM','3:30 PM',
      '4:00 PM','4:30 PM','5:00 PM','5:30 PM','6:00 PM'
    ];

    // If no date selected, use first open weekday hours as default
    let range = null;
    if (form.requested_date) {
      const d = new Date(form.requested_date + 'T12:00:00');
      range = getOpenCloseForDay(DAY_NAMES[d.getDay()]);
    } else if (hoursSettings) {
      for (const day of ['monday','tuesday','wednesday','thursday','friday','saturday','sunday']) {
        range = getOpenCloseForDay(day);
        if (range) break;
      }
    }

    if (!range) return { available: allSlots, blocked: new Set() };

    const filtered = allSlots.filter(slot => {
      const m = parseToMinutes(slot);
      return m !== null && m >= range.openMin && m < range.closeMin;
    });

    // Apply buffer percentage — block a contiguous chunk to appear like a real booking
    const bufPct = parseInt(hoursSettings && hoursSettings.bufferPercentage, 10) || 0;
    const blocked = new Set();
    if (bufPct > 0 && filtered.length > 1) {
      const removeCount = Math.floor(filtered.length * bufPct / 100);
      if (removeCount > 0) {
        // Deterministic seed from selected date so same block stays consistent per day
        const seed = form.requested_date
          ? form.requested_date.replace(/-/g, '').split('').reduce((a, c) => a + c.charCodeAt(0), 0)
          : 42;
        // Pick a random start index so the block doesn't always fall at the same time
        const maxStart = filtered.length - removeCount;
        const startIndex = seed % (maxStart + 1);
        for (let i = startIndex; i < startIndex + removeCount; i++) {
          blocked.add(filtered[i]);
        }
      }
    }

    return { available: filtered, blocked };
  };

  const { available: timeSlots, blocked: blockedSlots } = buildTimeSlots();

  // ── Min date = tomorrow ────────────────────────────────────────────────────
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  if (loading) {
    return (
      <div style={{ ...styles.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#666', fontFamily: "'Segoe UI', sans-serif" }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>⏳</div>
          <div style={{ fontWeight: 700 }}>Loading booking page...</div>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={{ ...styles.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#666', fontFamily: "'Segoe UI', sans-serif", padding: '40px' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>🔍</div>
          <div style={{ fontWeight: 900, fontSize: '20px', marginBottom: '8px' }}>Booking Page Not Found</div>
          <div style={{ fontSize: '14px' }}>This booking link may be invalid or expired.</div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.header}>
            {branding.logoUrl && (
              <img src={branding.logoUrl} alt={business.businessName} style={styles.logo} />
            )}
            <div style={styles.businessName}>{business.businessName}</div>
          </div>
          <div style={styles.success}>
            <div style={styles.successIcon}>✅</div>
            <div style={styles.successTitle}>Booking Request Received!</div>
            <div style={styles.successText}>
              Thank you! We received your booking request and will confirm your appointment shortly.
              Please check your email for a confirmation.
            </div>
            {(business.phone || business.email) && (
              <>
                <hr style={styles.divider} />
                <div style={{ fontSize: '13px', color: '#888', marginBottom: '8px' }}>Questions? Contact us:</div>
                <div style={styles.contactInfo}>
                  {business.phone && <span style={styles.contactItem}>📞 {business.phone}</span>}
                  {business.email && <span style={styles.contactItem}>✉️ {business.email}</span>}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>

        {/* ── Header ── */}
        <div style={styles.header}>
          {branding.logoUrl && (
            <img src={branding.logoUrl} alt={business.businessName} style={styles.logo} />
          )}
          <div style={styles.businessName}>{business.businessName}</div>
          {branding.tagline && <div style={styles.tagline}>{branding.tagline}</div>}
        </div>

        <div style={styles.body}>

          {/* ── Select Service ── */}
          <div style={styles.sectionTitle}>SELECT A SERVICE</div>

          {appointmentTypes.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#888', fontSize: '14px' }}>
              No services available for online booking at this time.
            </div>
          ) : (
            appointmentTypes.map(apt => {
              const isSelected = form.appointment_type_id === String(apt.id);
              return (
                <button
                  key={apt.id}
                  style={{
                    ...styles.serviceBtn,
                    borderColor: isSelected ? accentColor : '#ddd',
                    backgroundColor: isSelected ? '#f0fff4' : '#fff',
                    boxShadow: isSelected ? `0 0 0 2px ${accentColor}` : 'none'
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f8f8f8'; e.currentTarget.style.borderColor = accentColor; }}
                  onMouseLeave={e => { if (!isSelected) { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.borderColor = '#ddd'; } }}
                  onClick={() => setField('appointment_type_id', String(apt.id))}
                >
                  <div style={{ ...styles.colorDot, backgroundColor: apt.color || '#888' }} />
                  <span>{apt.name}</span>
                  {apt.duration && (
                    <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#888', fontWeight: 600 }}>
                      {apt.duration} min
                    </span>
                  )}
                </button>
              );
            })
          )}

          <hr style={styles.divider} />

          {/* ── Date & Time ── */}
          <div style={styles.sectionTitle}>SELECT DATE & TIME</div>

          <div style={styles.row2}>
            <div>
              <label style={styles.label}>Preferred Date *</label>
              <input
                style={styles.input}
                type="date"
                min={minDate}
                value={form.requested_date}
                onChange={e => {
                  const val = e.target.value;
                  setField('requested_date', val);
                  if (val && isDateDisabled(val)) {
                    setField('requested_time', '');
                  }
                }}
              />
              {form.requested_date && isDateDisabled(form.requested_date) && (
                <div style={{ color: '#dc2626', fontSize: '13px', fontWeight: 700, marginTop: '6px' }}>
                  ⚠️ We are closed on that day. Please select another date.
                </div>
              )}
              {hoursSettings && (
                <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                  {['sunday','monday','tuesday','wednesday','thursday','friday','saturday']
                    .filter(d => !hoursSettings[d] || String(hoursSettings[d]).toLowerCase() === 'closed')
                    .map(d => d.charAt(0).toUpperCase() + d.slice(1))
                    .join(', ') ? `Closed: ${['sunday','monday','tuesday','wednesday','thursday','friday','saturday']
                    .filter(d => !hoursSettings[d] || String(hoursSettings[d]).toLowerCase() === 'closed')
                    .map(d => d.charAt(0).toUpperCase() + d.slice(1))
                    .join(', ')}` : ''}
                </div>
              )}
            </div>
            <div>
              <label style={styles.label}>Preferred Time *</label>
              <select
                style={styles.input}
                value={form.requested_time}
                onChange={e => setField('requested_time', e.target.value)}
              >
                <option value="">Select a time...</option>
                {timeSlots.map(t => (
                  <option key={t} value={t} disabled={blockedSlots.has(t)} style={{ color: blockedSlots.has(t) ? '#bbb' : 'inherit' }}>
                    {blockedSlots.has(t) ? `${t} — Unavailable` : t}
                  </option>
                ))}
              </select>
              {form.requested_date && !isDateDisabled(form.requested_date) && timeSlots.length === 0 && (
                <div style={{ color: '#dc2626', fontSize: '13px', fontWeight: 700, marginTop: '6px' }}>
                  ⚠️ No available times for this day.
                </div>
              )}
            </div>
          </div>

          <hr style={styles.divider} />

          {/* ── Contact Info ── */}
          <div style={styles.sectionTitle}>YOUR CONTACT INFORMATION</div>

          <label style={styles.label}>Full Name *</label>
          <input
            style={styles.input}
            type="text"
            value={form.customer_name}
            onChange={e => setField('customer_name', e.target.value)}
            placeholder="Your full name"
          />

          <div style={styles.row2}>
            <div>
              <label style={styles.label}>Phone Number *</label>
              <input
                style={styles.input}
                type="tel"
                value={form.customer_phone}
                onChange={e => setField('customer_phone', formatPhone(e.target.value))}
                placeholder="(555) 555-5555"
              />
            </div>
            <div>
              <label style={styles.label}>Email Address *</label>
              <input
                style={styles.input}
                type="email"
                value={form.customer_email}
                onChange={e => setField('customer_email', e.target.value)}
                placeholder="you@example.com"
              />
            </div>
          </div>

          <hr style={styles.divider} />

          {/* ── Service Address ── */}
          <div style={styles.sectionTitle}>SERVICE ADDRESS</div>

          <label style={styles.label}>Street Address</label>
          <input
            style={styles.input}
            type="text"
            value={form.service_address}
            onChange={e => setField('service_address', e.target.value)}
            placeholder="123 Main Street"
          />

          <div style={styles.row3}>
            <div>
              <label style={styles.label}>City</label>
              <input
                style={styles.input}
                type="text"
                value={form.city}
                onChange={e => setField('city', e.target.value)}
                placeholder="City"
              />
            </div>
            <div>
              <label style={styles.label}>State</label>
              <input
                style={styles.input}
                type="text"
                value={form.state}
                onChange={e => setField('state', String(e.target.value).toUpperCase())}
                placeholder="FL"
                maxLength={2}
              />
            </div>
            <div>
              <label style={styles.label}>Zip</label>
              <input
                style={styles.input}
                type="text"
                value={form.zip}
                onChange={e => setField('zip', e.target.value)}
                placeholder="12345"
              />
            </div>
          </div>

          <hr style={styles.divider} />

          {/* ── Notes ── */}
          <div style={styles.sectionTitle}>ADDITIONAL NOTES</div>

          <label style={styles.label}>Describe what you need (optional)</label>
          <textarea
            style={styles.textarea}
            value={form.notes}
            onChange={e => setField('notes', e.target.value)}
            placeholder="Please describe the service you need, any access instructions, or anything else we should know..."
          />

          {/* ── Error ── */}
          {error && <div style={styles.error}>⚠️ {error}</div>}

          {/* ── Submit ── */}
          <button
            style={{ ...styles.submitBtn, opacity: submitting ? 0.7 : 1 }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = accentColor; e.currentTarget.style.color = '#000'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = primaryColor; e.currentTarget.style.color = accentColor; }}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? '⏳ SUBMITTING...' : '📅 REQUEST APPOINTMENT'}
          </button>

          {/* ── Footer contact ── */}
          {business.email && (
            <div style={{ textAlign: 'center', marginTop: '24px' }}>
              <div style={{ fontSize: '12px', color: '#aaa', marginBottom: '6px' }}>
                Prefer to email us?
              </div>
              <div style={styles.contactInfo}>
                <a href={`mailto:${business.email}`} style={{ ...styles.contactItem, textDecoration: 'none', color: primaryColor }}>
                  ✉️ {business.email}
                </a>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}